import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button, Chip, CircularProgress, Collapse, DialogContent, DialogTitle,
  FormControl, FormControlLabel, FormLabel,
  Radio, RadioGroup,
  Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { createChild, listCompanyList, setCompanyListMain } from './api';
import type { CompanyListItem, CompanyType } from './api';
import { apiErrorMessage } from '@/lib/errors';
import { AdminCredentialsDialog } from './AdminCredentialsDialog';
import type { AdminCredsView } from './AdminCredentialsDialog';
import { GlassDialog } from '@/components/GlassDialog';

// Extracted so a key prop can reset all child-form state when (open, companyId) changes.
// Each distinct key causes React to unmount+remount this component, clearing its local state.
function ChildCreatePanel({
  companyId,
  onCreated,
  onLoadList,
}: {
  companyId: number;
  onCreated: (creds: AdminCredsView) => void;
  onLoadList: (id: number) => void;
}) {
  const [showChildForm, setShowChildForm] = useState(false);
  const [childNameLa, setChildNameLa] = useState('');
  const [childNameEn, setChildNameEn] = useState('');
  const [childTaxCode, setChildTaxCode] = useState('');
  const [childCompanyType, setChildCompanyType] = useState<CompanyType>('accounting');
  const [childLoading, setChildLoading] = useState(false);

  function resetChildForm() {
    setChildNameLa('');
    setChildNameEn('');
    setChildTaxCode('');
    setChildCompanyType('accounting');
    setShowChildForm(false);
  }

  async function handleCreateChild(e: React.FormEvent) {
    e.preventDefault();
    setChildLoading(true);
    const body = {
      nameLa: childNameLa.trim(),
      nameEn: childNameEn.trim(),
      taxCode: childTaxCode.trim() || null,
      companyType: childCompanyType,
    };
    try {
      const res = await toast.promise(
        createChild(companyId, body),
        { loading: 'ກຳລັງສ້າງ child...', success: 'ສ້າງ child ສຳເລັດ', error: (e) => apiErrorMessage(e, 'ສ້າງ child ບໍ່ສຳເລັດ') },
      );
      onCreated({
        username: res.adminUsername,
        password: res.adminPassword,
        created: true,
        nameLa: body.nameLa,
        nameEn: body.nameEn,
        pathName: res.pathName,
      });
      resetChildForm();
      onLoadList(companyId);
    } catch {
      // toast.promise already displayed the error toast
    } finally {
      setChildLoading(false);
    }
  }

  const canSubmitChild = childNameLa.trim().length > 0 && childNameEn.trim().length > 0;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setShowChildForm((v) => !v)}
      >
        {showChildForm ? 'ຍົກເລີກ' : '+ ສ້າງ child'}
      </Button>
      <Collapse in={showChildForm} unmountOnExit>
        <Box
          component="form"
          onSubmit={(e) => { void handleCreateChild(e); }}
          sx={{ mt: 2, mb: 3, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        >
          <Stack spacing={2}>
            <TextField
              label="ຊື່ (ລາວ)"
              value={childNameLa}
              onChange={(e) => setChildNameLa(e.target.value)}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="ຊື່ (ອັງກິດ)"
              value={childNameEn}
              onChange={(e) => setChildNameEn(e.target.value)}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="TIN"
              value={childTaxCode}
              onChange={(e) => setChildTaxCode(e.target.value)}
              fullWidth
              size="small"
            />
            <FormControl>
              <FormLabel>ປະເພດ</FormLabel>
              <RadioGroup
                row
                value={childCompanyType}
                onChange={(e) => setChildCompanyType(e.target.value as CompanyType)}
              >
                <FormControlLabel value="accounting" control={<Radio size="small" />} label="ບັນຊີ" />
                <FormControlLabel value="tax" control={<Radio size="small" />} label="ພາສີ" />
              </RadioGroup>
            </FormControl>
            <Button
              type="submit"
              variant="contained"
              loading={childLoading}
              disabled={!canSubmitChild}
              sx={{ alignSelf: 'flex-start' }}
            >
              ສ້າງ child
            </Button>
          </Stack>
        </Box>
      </Collapse>
    </>
  );
}

export function CompanyListDialog({
  companyId, open, onClose, basePath = '/api/companies', showCreateChild = true, onChanged,
}: { companyId: number | null; open: boolean; onClose: () => void; basePath?: string; showCreateChild?: boolean; onChanged?: () => void }) {
  const [rows, setRows] = useState<CompanyListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // credentials dialog
  const [credsOpen, setCredsOpen] = useState(false);
  const [createdCreds, setCreatedCreds] = useState<AdminCredsView | null>(null);

  async function loadList(id: number) {
    setLoading(true);
    setError(null);
    try {
      const data = await listCompanyList(id, basePath);
      setRows(data);
    } catch {
      setRows([]);
      setError('ບໍ່ສາມາດໂຫຼດລາຍຊື່ກຸ່ມໄດ້');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const id = companyId;
    if (!open || id == null) return;
    let active = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await listCompanyList(id as number, basePath);
        if (active) setRows(data);
      } catch {
        if (active) { setRows([]); setError('ບໍ່ສາມາດໂຫຼດລາຍຊື່ກຸ່ມໄດ້'); }
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => { active = false; };
  }, [open, companyId, basePath]);

  async function handleSetMain(row: CompanyListItem) {
    if (companyId == null || row.databaseName == null) return;
    if (!window.confirm('ທ່ານແນ່ໃຈບໍ?')) return;
    setBusy(true);
    try {
      await toast.promise(
        setCompanyListMain(companyId, row.databaseName, basePath),
        { loading: 'ກຳລັງຕັ້ງ Main...', success: 'ຕັ້ງ Main ສຳເລັດ', error: (e) => apiErrorMessage(e, 'ຕັ້ງ Main ບໍ່ສຳເລັດ') },
      );
    } catch {
      // toast.promise already displayed the error toast
      setBusy(false);
      return;
    }
    onChanged?.(); // the group's Main/Child flags changed — let the parent grid refresh
    // PUT succeeded; refetch to reflect the multi-row Main/Child change
    setError(null);
    try {
      const data = await listCompanyList(companyId, basePath);
      setRows(data);
    } catch {
      // backend changed but reload failed — show the error view, not stale rows
      setRows([]);
      setError('ຕັ້ງ Main ສຳເລັດ ແຕ່ໂຫຼດລາຍຊື່ຄືນບໍ່ໄດ້');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <GlassDialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          ລາຍຊື່ບໍລິສັດ (ກຸ່ມ)
        </DialogTitle>
        <DialogContent>
          {showCreateChild && companyId != null && (
            // key={`${open}-${companyId}`} resets all ChildCreatePanel state on each (re)open or
            // companyId change — prevents stale form input from being submitted under a different parent.
            <ChildCreatePanel
              key={`${open}-${companyId}`}
              companyId={companyId}
              onCreated={(res) => { setCreatedCreds(res); setCredsOpen(true); onChanged?.(); }}
              onLoadList={loadList}
            />
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? (
            <CircularProgress size={28} sx={{ display: 'block', mx: 'auto', my: 3 }} />
          ) : rows.length === 0 ? (
            !error && <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>ບໍ່ມີຂໍ້ມູນ</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ບໍລິສັດ</TableCell>
                    <TableCell>ຖານຂໍ້ມູນ</TableCell>
                    <TableCell>ປະເພດ</TableCell>
                    <TableCell align="right">ດຳເນີນການ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.companyName ?? '—'}</TableCell>
                      <TableCell>{row.databaseName ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.companyType ?? '—'}
                          color={row.companyType === 'Main' ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {row.companyType !== 'Main' && row.databaseName !== null ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busy}
                            onClick={() => { void handleSetMain(row); }}
                          >
                            ຕັ້ງເປັນ Main
                          </Button>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </GlassDialog>

      <AdminCredentialsDialog
        creds={createdCreds}
        open={credsOpen}
        onClose={() => { setCredsOpen(false); setCreatedCreds(null); }}
      />
    </>
  );
}
